import React, { createContext, useContext, useState, ReactNode } from "react";

// Define the structure of the error state
interface ErrorState {
  message: string;
  visible: boolean;
}

// Define the shape of the context
interface ErrorContextType {
  error: ErrorState;
  showError: (message: string) => void;
  hideError: () => void;
}

// Create the context with a default value
const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

// Provider component props
interface ErrorProviderProps {
  children: ReactNode;
}

// ErrorProvider to wrap the application
export const ErrorProvider = ({ children }: ErrorProviderProps) => {
  const [error, setError] = useState<ErrorState>({
    message: "",
    visible: false,
  });

  const showError = (message: string) => setError({ message, visible: true });
  const hideError = () => setError({ ...error, visible: false });

  return (
    <ErrorContext.Provider value={{ error, showError, hideError }}>
      {children}
    </ErrorContext.Provider>
  );
};

// Hook to use the error context
export const useErrorContext = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error("useErrorContext must be used within an ErrorProvider");
  }
  return context;
};
